import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Card,
  CardContent,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Upload as UploadIcon,
  Notifications as NotificationsIcon,
  Folder as FolderIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format, parseISO, startOfDay, subDays, eachDayOfInterval } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { generateClient } from 'aws-amplify/api';
import { clientVendorsByClient } from 'graphql/vendor';
import { getVendorActivities } from 'services/activity';

const API = generateClient();

export default function ClientActivityDashboard({ selectedClient, vendorLabel }) {
  const [loading, setLoading] = useState(true);
  const [allActivities, setAllActivities] = useState([]);
  const [vendorMap, setVendorMap] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Fetch all vendors and their activities for the client
  useEffect(() => {
    const fetchClientActivities = async () => {
      if (!selectedClient?.clientID) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch all vendors for this client
        const vendorResponse = await API.graphql({
          query: clientVendorsByClient,
          variables: { clientID: selectedClient.clientID }
        });

        const vendorItems = vendorResponse?.data?.clientVendorsByClient?.items || [];

        // Create vendor map for quick lookup
        const vMap = {};
        vendorItems.forEach(item => {
          if (item.vendor) {
            vMap[item.id] = {
              id: item.id,
              name: item.vendor.name || item.vendor.entityName || 'Unknown Vendor',
              vendorId: item.vendor.id
            };
          }
        });
        setVendorMap(vMap);

        // Fetch activities for each vendor
        const allActivitiesPromises = vendorItems.map(async (item) => {
          if (item.id) {
            const activities = await getVendorActivities(item.id);
            // Add vendor info to each activity
            return activities.map(activity => ({
              ...activity,
              clientVendorID: item.id,
              vendorName: vMap[item.id]?.name || 'Unknown Vendor'
            }));
          }
          return [];
        });

        const activitiesArrays = await Promise.all(allActivitiesPromises);
        const flattenedActivities = activitiesArrays.flat();

        setAllActivities(flattenedActivities);
      } catch (error) {
        console.error('Error fetching client activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientActivities();
  }, [selectedClient?.clientID]);

  const handleOpenDrawer = (type) => {
    setDrawerType(type);
    setPage(0);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerType(null);
    setPage(0);
  };

  // Calculate statistics and time series data
  const { stats, chartData, dateRange } = useMemo(() => {
    if (!allActivities || allActivities.length === 0) {
      return { stats: { requests: 0, reminders: 0, certificateUploads: 0, otherDocuments: 0 }, chartData: [], dateRange: null };
    }

    // Filter relevant activities
    const relevantActivities = allActivities.filter(activity =>
      ['EMAIL_SEND', 'REQUEST_CERTIFICATE', 'REQUEST_DOCUMENT', 'CERTIFICATE_CREATION', 'DOCUMENT_CREATION'].includes(activity.action)
    );

    if (relevantActivities.length === 0) {
      return { stats: { requests: 0, reminders: 0, certificateUploads: 0, otherDocuments: 0 }, chartData: [], dateRange: null };
    }

    // Calculate stats
    const counts = {
      requests: relevantActivities.filter(a => a.action === 'REQUEST_CERTIFICATE' || a.action === 'REQUEST_DOCUMENT').length,
      reminders: relevantActivities.filter(a => a.action === 'EMAIL_SEND').length,
      certificateUploads: relevantActivities.filter(a => a.action === 'CERTIFICATE_CREATION').length,
      otherDocuments: relevantActivities.filter(a => a.action === 'DOCUMENT_CREATION').length
    };

    // Get date range (last 30 days or from first activity)
    const now = new Date();
    const activityDates = relevantActivities.map(a => parseISO(a.createdAt));
    const oldestActivity = new Date(Math.min(...activityDates));
    const startDate = subDays(now, 200); // Extended to 200 days for better visibility
    const rangeStart = oldestActivity < startDate ? oldestActivity : startDate;

    // Create array of all days in range
    const allDays = eachDayOfInterval({ start: rangeStart, end: now });

    // Group activities by day
    const activityByDay = {};
    allDays.forEach(day => {
      const dayKey = format(startOfDay(day), 'yyyy-MM-dd');
      activityByDay[dayKey] = {
        date: dayKey,
        requests: 0,
        reminders: 0,
        certificateUploads: 0,
        otherDocuments: 0
      };
    });

    // Count activities per day
    relevantActivities.forEach(activity => {
      const activityDate = startOfDay(parseISO(activity.createdAt));
      const dayKey = format(activityDate, 'yyyy-MM-dd');

      if (activityByDay[dayKey]) {
        if (activity.action === 'REQUEST_CERTIFICATE' || activity.action === 'REQUEST_DOCUMENT') {
          activityByDay[dayKey].requests += 1;
        } else if (activity.action === 'EMAIL_SEND') {
          activityByDay[dayKey].reminders += 1;
        } else if (activity.action === 'CERTIFICATE_CREATION') {
          activityByDay[dayKey].certificateUploads += 1;
        } else if (activity.action === 'DOCUMENT_CREATION') {
          activityByDay[dayKey].otherDocuments += 1;
        }
      }
    });

    // Convert to array and format for chart
    const chartArray = Object.values(activityByDay).map(day => ({
      date: day.date,
      displayDate: format(parseISO(day.date), 'MMM dd'),
      Requests: day.requests,
      Reminders: day.reminders,
      'Certificate Uploads': day.certificateUploads,
      'Other Documents': day.otherDocuments
    }));

    return {
      stats: counts,
      chartData: chartArray,
      dateRange: {
        start: format(rangeStart, 'MMM dd'),
        end: format(now, 'MMM dd, yyyy')
      }
    };
  }, [allActivities]);

  // Filter activities for drawer based on type
  const filteredActivities = useMemo(() => {
    if (!drawerType || !allActivities) return [];

    const actionMap = {
      'requests': ['REQUEST_CERTIFICATE', 'REQUEST_DOCUMENT'],
      'reminders': ['EMAIL_SEND'],
      'certificateUploads': ['CERTIFICATE_CREATION'],
      'otherDocuments': ['DOCUMENT_CREATION']
    };

    const relevantActions = actionMap[drawerType] || [];
    return allActivities
      .filter(activity => relevantActions.includes(activity.action))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [allActivities, drawerType]);

  const paginatedActivities = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredActivities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActivities, page]);

  const getDrawerTitle = () => {
    const titles = {
      'requests': 'Requests',
      'reminders': 'Reminders',
      'certificateUploads': 'Certificate Uploads',
      'otherDocuments': 'Other Documents'
    };
    return titles[drawerType] || 'Activities';
  };

  const getActivityIcon = () => {
    const icons = {
      'requests': <EmailIcon />,
      'reminders': <NotificationsIcon />,
      'certificateUploads': <UploadIcon />,
      'otherDocuments': <FolderIcon />
    };
    return icons[drawerType];
  };

  // Custom tooltip for the chart - shows vendor info
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = payload[0]?.payload?.displayDate;
      const dateKey = payload[0]?.payload?.date;

      // Get activities for this specific date
      const dateActivities = allActivities.filter(activity => {
        const activityDate = format(startOfDay(parseISO(activity.createdAt)), 'yyyy-MM-dd');
        return activityDate === dateKey;
      });

      // Group by vendor and count by type
      const vendorCounts = {};
      dateActivities.forEach(activity => {
        const vendor = activity.vendorName || 'Unknown';
        if (!vendorCounts[vendor]) {
          vendorCounts[vendor] = {
            requests: 0,
            reminders: 0,
            certificateUploads: 0,
            otherDocuments: 0
          };
        }
        if (activity.action === 'REQUEST_CERTIFICATE' || activity.action === 'REQUEST_DOCUMENT') {
          vendorCounts[vendor].requests += 1;
        } else if (activity.action === 'EMAIL_SEND') {
          vendorCounts[vendor].reminders += 1;
        } else if (activity.action === 'CERTIFICATE_CREATION') {
          vendorCounts[vendor].certificateUploads += 1;
        } else if (activity.action === 'DOCUMENT_CREATION') {
          vendorCounts[vendor].otherDocuments += 1;
        }
      });

      return (
        <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0', maxWidth: 300 }} elevation={3}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {date}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {payload.map((entry, index) => (
            entry.value > 0 && (
              <Typography
                key={`item-${index}`}
                variant="body2"
                sx={{ color: entry.color, fontSize: '0.875rem', fontWeight: 600 }}
              >
                {entry.name}: {entry.value}
              </Typography>
            )
          ))}
          {Object.keys(vendorCounts).length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                By {vendorLabel || 'Subcontractor'}:
              </Typography>
              {Object.entries(vendorCounts).map(([vendor, counts]) => {
                const total = counts.requests + counts.reminders + counts.certificateUploads + counts.otherDocuments;
                return total > 0 && (
                  <Typography key={vendor} variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    • {vendor}: {total}
                  </Typography>
                );
              })}
            </>
          )}
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading activity data...
        </Typography>
      </Paper>
    );
  }

  if (!allActivities || allActivities.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Activity Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet for this client.
        </Typography>
      </Paper>
    );
  }

  if (chartData.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Activity Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No email, request, or upload activity to display for this client.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6">
            {selectedClient?.name || 'Client'} Activity Dashboard
          </Typography>
          {dateRange && (
            <Typography variant="body2" color="text.secondary">
              {dateRange.start} - {dateRange.end}
            </Typography>
          )}
        </Stack>

        {/* Statistics Cards */}
        <Stack className="activity-dashboard-stats" direction="row" spacing={2} sx={{ mb: 3 }}>
          <Card variant="outlined" sx={{ flex: 1, bgcolor: '#e3f2fd', cursor: 'pointer' }} onClick={() => handleOpenDrawer('requests')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {stats.requests}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requests
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color: '#1976d2' }}>
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, bgcolor: '#fff4e5', cursor: 'pointer' }} onClick={() => handleOpenDrawer('reminders')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <NotificationsIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {stats.reminders}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reminders
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color: '#ed6c02' }}>
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, bgcolor: '#e8f5e9', cursor: 'pointer' }} onClick={() => handleOpenDrawer('certificateUploads')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UploadIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {stats.certificateUploads}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Certificate Uploads
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color: '#2e7d32' }}>
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, bgcolor: '#f3e5f5', cursor: 'pointer' }} onClick={() => handleOpenDrawer('otherDocuments')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FolderIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {stats.otherDocuments}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Other Documents
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color: '#9c27b0' }}>
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Chart */}
        <Box className="activity-dashboard-chart" sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="Requests"
                stroke="#1976d2"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Reminders"
                stroke="#ed6c02"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Certificate Uploads"
                stroke="#2e7d32"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Other Documents"
                stroke="#9c27b0"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Drawer for detailed activities */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: 500 } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getActivityIcon()}
              <Typography variant="h6">
                {getDrawerTitle()}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total: {filteredActivities.length} activities
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <List>
            {paginatedActivities.map((activity, index) => {
              let displayText = activity.userFullName || 'System';
              let actionText = activity.action.replace(/_/g, ' ').toLowerCase();
              actionText = actionText.charAt(0).toUpperCase() + actionText.slice(1);

              return (
                <React.Fragment key={activity.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {actionText}
                          </Typography>
                          <Chip
                            label={activity.vendorName}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" component="span" color="text.secondary">
                            {displayText} • {format(parseISO(activity.createdAt), 'MMM dd, yyyy h:mm a')}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < paginatedActivities.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>

          {filteredActivities.length > ITEMS_PER_PAGE && (
            <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                size="small"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                Page {page + 1} of {Math.ceil(filteredActivities.length / ITEMS_PER_PAGE)}
              </Typography>
              <Button
                size="small"
                disabled={(page + 1) * ITEMS_PER_PAGE >= filteredActivities.length}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
}
